import { PlayerHookType } from '@cbsinteractive/avia-js';
import { HookInterface } from '@cbsinteractive/avia-js/dist/types/iface/HookInterface';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { ITagEventDataVO } from 'tag/ts/tag/interfaces/events/ITagEventDataVO';
import { IAviaPlayerHook } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaPlayerHook';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad
 * @description Base class for VTG Avia player hook implementations
 *
 * **VTG Hook Integration Pattern:**
 * Provides standardized interface for creating VTG player hooks that can modify
 * player behavior and data in real-time. Hooks are callback functions registered
 * with VTG player that execute during specific player events.
 *
 * **Hook Implementation Requirements:**
 * - **hookType**: PlayerHookType enum value (AD, AUDIO, VIDEO, etc.)
 * - **callback**: HookInterface function that processes hook context data
 *
 * **Hook Registration Lifecycle:**
 * 1. **Hook Creation**: Subclass instantiation triggers hook setup
 * 2. **Registration Request**: Dispatches ON_PLAYER_HOOK_REQUEST_REGISTER event
 * 3. **Manager Processing**: AviaPlayerHooksManager handles registration with VTG
 * 4. **Hook Execution**: VTG player calls hook during relevant events
 * 5. **Cleanup**: Hooks removed during player teardown
 *
 * **Smart Tag Event Integration:**
 * Uses Smart Tag event system for hook lifecycle management, ensuring hooks
 * are properly coordinated with Smart Tag's component architecture.
 *
 * **Business Impact:**
 * Enables Smart Tag to customize VTG player behavior for specific requirements
 * without modifying VTG core code, maintaining upgrade compatibility.
 */
export abstract class AbstractAviaPlayerHook extends AbstractTagClass implements IAviaPlayerHook {

	// properties which mush be defined by player hook implementation classes
	abstract hookType: PlayerHookType;
	abstract callback: HookInterface;

	protected constructor( argument: ITagAbstractClassArgument ) {
		super( argument );
	}

	/**
	 * @description Requests hook registration with VTG Avia player via Smart Tag event system
	 *
	 * **Registration Process:**
	 * Dispatches Smart Tag event to AviaPlayerHooksManager which handles the actual
	 * VTG player registration. This indirect approach maintains Smart Tag's event
	 * architecture and ensures proper lifecycle management.
	 *
	 * **Event Flow:**
	 * Hook → ON_PLAYER_HOOK_REQUEST_REGISTER → AviaPlayerHooksManager → VTG Player
	 */
	registerHook = (): void => {
		// notify app
		this.dispatchTagEvent(
			this.getTagEventVO(
				TagEvent.ON_PLAYER_HOOK_REQUEST_REGISTER,
				this.tag,
				{
					playerHookRequestRegister: {
						hook: this
					}
				} as ITagEventDataVO,
				this
			)
		);
	};

	/**
	 * @description Requests hook removal from VTG Avia player via Smart Tag event system
	 *
	 * **Removal Process:**
	 * Dispatches Smart Tag event to AviaPlayerHooksManager which handles the actual
	 * VTG player unregistration. Ensures clean hook removal during player cleanup.
	 *
	 * **Event Flow:**
	 * Hook → ON_PLAYER_HOOK_REQUEST_REMOVE → AviaPlayerHooksManager → VTG Player
	 */
	removeHook = (): void => {
		// notify app
		this.dispatchTagEvent(
			this.getTagEventVO(
				TagEvent.ON_PLAYER_HOOK_REQUEST_REMOVE,
				this.tag,
				{
					playerHookRequestRemove: {
						hook: this
					}
				} as ITagEventDataVO,
				this
			)
		);
	};

	/**
	 * @description Remove hook
	 */
	cleanUp = (): void => {
		this.removeHook();
	};

}